// filepath: apps/web/src/features/admin/templates/export-import/ImportDialog.tsx
//
// FR-UI-ADM-13 AC-2 / AC-10 — modal containing the inline
// "Use the operator console to import templates" notice, the
// file picker, and (when a file is picked) the parsed-manifest
// preview + per-line validation error rendering + the
// cross-publisher-kind guard card.
//
// SCOPE DECISION (Q1, embedded in 01-requirement-validation.md):
//   The Submit import button is rendered DISABLED with a tooltip
//   pointing to the operator console. The SPA does NOT POST to
//   /admin/templates/import. The file picker + FileReader parse
//   path is purely informational — it shows the operator what
//   the bundle looks like before they walk over to the operator
//   console to upload it.
//
// INV-5 (TRANSIENT ONLY):
//   The parsed manifest from `useTemplateBundleImportPreview` is
//   read for the validation card rendering + the Replace prompt,
//   then DISCARDED when the dialog closes (`reset()` clears the
//   hook state). The parsed manifest is NEVER persisted in
//   component state beyond the dialog's open lifetime. The
//   contract is documented in the file header of
//   `useTemplateBundleImportPreview.ts` and reviewed by
//   SecurityReviewer.
//
// FR-UI-CORE-9 (focus trap): the dialog mounts
//   `apps/web/src/runtime/focusTrap.ts` on the modal container.
//   Escape dismisses the dialog.

import React, { useEffect, useRef } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Dialog } from "@/design-system/Dialog";
import { focusTrap } from "@/runtime/focusTrap";
import { useTemplateBundleImportPreview } from "./useTemplateBundleImportPreview";
import type { TemplateRegistryRow } from "../registry/types";

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The current registry list (used to detect a `template_id`
   * collision when the parsed manifest matches an existing row,
   * which would then open the `ReplaceTemplateDialog` via
   * `onReplaceExisting`).
   */
  existingTemplates: TemplateRegistryRow[];
  onReplaceExisting: (row: TemplateRegistryRow) => void;
}

export function ImportDialog({
  open,
  onClose,
  existingTemplates,
  onReplaceExisting,
}: ImportDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { preview, error, parse, reset, isParsing } =
    useTemplateBundleImportPreview();

  // Mount the focus trap when the dialog opens; teardown when it
  // closes. Mirrors the FR-UI-CORE-9 contract.
  useEffect(() => {
    if (!open) return;
    const container = dialogRef.current;
    if (!container) return;
    const teardown = focusTrap(container);
    return teardown;
  }, [open]);

  // Reset the hook state when the dialog closes — the parsed
  // manifest is TRANSIENT per INV-5.
  useEffect(() => {
    if (!open) {
      reset();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open, reset]);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (file) {
      parse(file);
    }
  };

  // If the parsed manifest's template_id collides with an existing
  // row, surface the Replace prompt via the parent (parent closes
  // this dialog and opens ReplaceTemplateDialog).
  useEffect(() => {
    if (!preview) return;
    const collision = existingTemplates.find(
      (row) => row.template_id === preview.template_id,
    );
    if (collision) {
      onReplaceExisting(collision);
    }
  }, [preview, existingTemplates, onReplaceExisting]);

  return (
    <Dialog
      open={open}
      title={t("admin.templates.export_import.dialog.import.title")}
      onClose={onClose}
      data-testid="template-bundle-import-dialog"
    >
      <div ref={dialogRef} className="space-y-3 text-sm">
        {/* Inline operator-console-required notice (Q1). */}
        <div
          className="rounded border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900"
          role="alert"
          data-testid="template-bundle-import-operator-console-notice"
        >
          {t("admin.templates.export_import.notice.operator_console_required")}
        </div>

        {/* Hidden file input. */}
        <label
          className="block text-xs font-medium"
          data-testid="template-bundle-import-file-label"
        >
          {t("admin.templates.export_import.dialog.import.file_label")}
          <input
            ref={fileInputRef}
            type="file"
            accept=".ascoa-template,.zip,application/zip,application/octet-stream"
            onChange={handleFileChange}
            disabled={isParsing}
            className="mt-1 block w-full text-xs"
            data-testid="template-bundle-import-file-input"
          />
        </label>

        {/* Parsed-manifest preview card. Only renders if parse
              succeeded; the parsed object is read here and then
              DISCARDED on dialog close (the hook's reset()). */}
        {preview ? (
          <div
            className="rounded border border-border bg-background p-3 text-xs"
            data-testid="template-bundle-import-preview"
          >
            <div>
              <strong>{preview.template_id}</strong> v{preview.version}
            </div>
            <div className="text-muted-foreground">{preview.description}</div>
          </div>
        ) : null}

        {/* Error card. */}
        {error ? (
          <div
            className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700"
            role="alert"
            data-testid={`template-bundle-import-error-${error.kind}`}
          >
            {renderErrorMessage(error, t)}
          </div>
        ) : null}

        {/* Submit import — DISABLED per Q1. */}
        <button
          type="button"
          disabled
          title={t(
            "admin.templates.export_import.dialog.import.submit_disabled_tooltip",
          )}
          className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="template-bundle-import-submit"
        >
          {t("admin.templates.export_import.dialog.import.submit")}
        </button>
      </div>
    </Dialog>
  );
}

function renderErrorMessage(
  error:
    | { kind: "manifest_missing"; message: string }
    | { kind: "manifest_invalid_json"; message: string }
    | { kind: "cross_publisher_kind"; message: string }
    | { kind: "manifest_incomplete"; message: string; missingFields: string[] }
    | { kind: "file_pick_failed"; message: string },
  t: (key: string) => string,
): string {
  switch (error.kind) {
    case "manifest_missing":
      return t("admin.templates.export_import.errors.manifest_missing");
    case "manifest_invalid_json":
      return t("admin.templates.export_import.errors.manifest_invalid_json");
    case "cross_publisher_kind":
      return t("admin.templates.export_import.errors.cross_publisher_kind");
    case "manifest_incomplete":
      return t("admin.templates.export_import.errors.manifest_incomplete");
    case "file_pick_failed":
      return t("admin.templates.export_import.errors.file_pick_failed");
  }
}
